import React, { useState, useRef, useCallback, useEffect, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
/* --------------------------------------------- */
import Spinner from '.././Spinner'
/* --------------------------------------------- */
import { routeMap, routeVarMap } from '../../route'
/* --------------------------------------------- */
import useLoggly from '../../containers/useLoggly'
import useAnalytics from '../../containers/useAnalytics'
/* --------------------------------------------- */
import { GlobalContext } from '../../context/global.context'
import { ErrorContext } from '../../context/error.context'
/* --------------------------------------------- */
import useGeneralApi from '../../apis/useGeneralApi'//
import useDetailsApi from '../../apis/useDetailsApi'
/* --------------------------------------------- */
import useFirebaseTrace from '../../hooks/useFirebaseTrace'
/* --------------------------------------------- */
import SearchIcon from '.././icons/SearchIcon'//
import GoBackArrowIcon from '.././icons/GoBackArrowIcon'//
import utils from '../../utils/utils'//
import { splitLanguage } from '../../utils/localStorageService'//
import { getRecentSearchesLocal, setRecentSearchesLocal } from '../../utils/sessionStorageService'
import { StyleWrapper, SearchInputOuter, ListTitle, ClearAll, ListItemTitle, ListItemSubtitle, MoreResults } from './style'
/* --------------------------------------------- */
// SearchBar component for showing search bar and handling search events.
const SearchBar = () => {
  const [isShowMore, toggleIsShowMore] = useState(false)
  const [isFocus, toggleIsFocus] = useState(false)
  const [suggestions, updateSuggestions] = useState([])
  const [noMatch, toggleNoMatch] = useState(false)
  const [searchBarTheme, setSearchBarTheme] = useState({})
  const [isResponsiveSearchOpen, setIsResponsiveSearchOpen] = useState(false)
  const [noRecentSearch, toggleNoRecentSearch] = useState(false)
  const [searchIconColor, setSearchIconColor] = useState('transparent')
  const [searchHistoryTheme, setSearchHistoryTheme] = useState({})
  const [searchSuggestionTheme, setSearchSuggestionTheme] = useState({})
  const [sysId, setSysId] = useState('')
  const [recentSearches, setRecentSearches] = useState([])
  /* --------------------------------------------- */
  const { isAuthenticated, searchText, dispatch, appLanguage, selectedProfile } = useContext(GlobalContext)
  const { errorPage } = useContext(ErrorContext)
  /* --------------------------------------------- */
  const { sendAnalytics } = useAnalytics()
  const { getSearchSuggestion } = useGeneralApi()
  const { setRecentSearch, getRecentSearch, clearRecentSearch } = useDetailsApi()
  /* --------------------------------------------- */
  const { specialMenuConfig } = useSelector(store => store.navigation)
  const { appConfig, appMessages } = useSelector(store => store.appConfigReducers)
  const { searchConfig, searchComponents } = useSelector(store => store.search)
  /* --------------------------------------------- */
  const history = useHistory()
  const searchTimer = useRef(null)
  const appMessagesRef = useRef(appMessages)
  /* --------------------------------------------- */
  const { log } = useLoggly()
  /* --------------------------------------------- */
  const firebaseTrace = useFirebaseTrace()
  /* --------------------------------------------- */
  const searchBar = useRef(null)
  const searchInput = useRef(null)
  /* --------------------------------------------- */
  /**
   * It: Handles outside click.
   * It: Removes focus.
   */
  const handleClickOutside = useCallback((event) => {
    if (!(searchBar.current && searchBar.current.contains(event.target))) {
      toggleIsFocus(false)
    }
  }, [])
  /* --------------------------------------------- */
  /**
   * It: Handles search input change.
   * It: Sets search text in state.
   */
  const searchInputChangeHandler = useCallback((e) => {
    searchInput.current.focus()

    const value = e ? e.target.value : e
    if (value.length > searchConfig.maximumSearchQueryLength) return

    /* --------------------------------------------- */
    dispatch({ type: 'SEARCH_TEXT', value })
  }, [
    searchConfig.maximumSearchQueryLength, dispatch
  ])
  /* --------------------------------------------- */
  /**
   * It: Sets recent search from local on focus.
   */
  useEffect(() => {
    appMessagesRef.current = appMessages
  }, [appMessages])
  /* --------------------------------------------- */
  useEffect(() => {
    if (isFocus) {
      const recentSearchesLocal = getRecentSearchesLocal() || []
      setRecentSearches(recentSearchesLocal)
    }
  }, [isFocus])
  /* --------------------------------------------- */
  /**
   * It: Updates theme.
   */
  useEffect(() => {
    if (Object.keys(searchComponents).length === 0) return
    const { theme: historyTheme = {} } = searchComponents['component_search_history'] || {}
    const { theme: suggestionTheme = {} } = searchComponents['component_search_suggestion'] || {}
    setSearchHistoryTheme(historyTheme)
    setSearchSuggestionTheme(suggestionTheme)
  }, [searchComponents])
  /* --------------------------------------------- */
  /**
   * It: Updaets sysId in a state.
   */
  useEffect(() => {
    const searchPage = searchConfig.searchPage || []
    const { sysId = '' } = searchPage[0] || {}
    setSysId(sysId)
  }, [searchConfig.searchPage])
  /* --------------------------------------------- */
  /**
   * It: Sets search bar theme.
   */
  useEffect(() => {
    const searchBarItem = specialMenuConfig.filter(({ id }) => {
      return (id === 'search_bar')
    })
    if (utils.isNotNull(searchBarItem, 0, 'buttonStyle')) {
      setSearchBarTheme(searchBarItem[0].buttonStyle)
    }
  }, [
    specialMenuConfig
  ])
  /* --------------------------------------------- */
  /**
   * It: Sets mouse down event listerner.
   */
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [
    handleClickOutside
  ])
  /* --------------------------------------------- */
  /**
   * It: Fetches recent search from MW when authenticated
   * It: Sends performance trace.
   * It: Toggles recent search dropdown.
   */
  useEffect(() => {
    let isMounted = true
    let trace = null
    if (isAuthenticated) {
      if (isFocus && searchText.length < searchConfig.minimumSearchQueryLength && searchConfig.enableSearchHistory && appConfig.baseUrl) {
        trace = firebaseTrace('recent_search_load')
        let profileId = selectedProfile ? selectedProfile.profileId : ''
        getRecentSearch(appConfig.baseUrl, searchConfig.maxSearchHistoryCount, profileId)
          .then((res) => {
            if (!isMounted) return
            let { searchList } = res
            searchList = [...new Set(searchList)]
            searchList = searchList.slice(0, searchConfig.maxSearchHistoryCount)
            setRecentSearchesLocal(searchList)
            if (searchList.length === 0) toggleNoRecentSearch(true)
            else toggleNoRecentSearch(false)
            trace.addAttrs({ itemCount: (searchList || []).length + '' })
            trace.stop()
            trace = null
          })
          .catch(() => {
            if (!isMounted) return
            toggleNoRecentSearch(true)
            trace.stop()
            trace = null
          })
      }
    } else {
      toggleNoRecentSearch(true)
    }
    return () => {
      isMounted = false
      if (trace) trace.stop()
    }
  }, [isFocus,
    searchText,
    isAuthenticated,
    getRecentSearch,
    setRecentSearches,
    appConfig.baseUrl,
    searchConfig.enableSearchHistory,
    searchConfig.maxSearchHistoryCount,
    searchConfig.minimumSearchQueryLength,
    selectedProfile,
    firebaseTrace
  ])
  /* --------------------------------------------- */
  /**
   * It: Fetches search suggetino from MW.
   * It: Sends performance trace.
   */
  useEffect(() => {

    updateSuggestions([])
    toggleNoMatch(false)
    let isMounted = true
    let trace = null
    if (searchTimer.current) {
      clearTimeout(searchTimer.current)
    }
    searchTimer.current = setTimeout(() => {
      if (
        isFocus &&
        searchText &&
        appConfig.baseUrl &&
        searchText.trim().length >= searchConfig.minimumSearchQueryLength
        // !/^\s/.test(searchText)
      ) {
       
        /* --------------------------------------------- */
        log({
          type: 'Info',
          attr: {
            event: 'search_text',
            module: 'Search',
            search_term: searchText
          }
        })
        /* --------------------------------------------- */
        toggleIsShowMore(false)
        /* --------------------------------------------- */
        trace = firebaseTrace('search_suggestions_load', {
          searchTerm: searchText
        })
        /* --------------------------------------------- */
        getSearchSuggestion(
          appConfig.baseUrl,
          {
            query: window.encodeURIComponent(searchText),
            maxResultCount: searchConfig.maxSearchSuggestionCount,
            ratio: searchConfig.searchSuggestionTypeRatio,
            language: splitLanguage(appLanguage),
            profileId: selectedProfile ? selectedProfile.profileId : ''
          }
        ).then((res) => {
          if (!isMounted) return
          const content = res.content || []
          const newArray = content.slice(0, searchConfig.maxSearchSuggestionCount)
          updateSuggestions(newArray)
          /* --------------------------------------------- */
          if (content.length > 0) toggleIsShowMore(true)
          /* --------------------------------------------- */
          if (content.length === 0) toggleNoMatch(true)
          else toggleNoMatch(false)
          trace.addAttrs({ resultCount: (newArray || []).length + '' });
          trace.stop()
          trace = null
        }).catch(() => {
          updateSuggestions([])
          toggleNoMatch(false)
          trace.stop()
          trace = null
        })
      } else {
        updateSuggestions([])
      }
    }, 1300)

    return () => {
      isMounted = false
      clearTimeout(searchTimer.current)
      if (trace) trace.stop()
    }
  }, [appLanguage,
    isFocus,
    searchText,
    log,
    appConfig.baseUrl,
    getSearchSuggestion,
    searchConfig.minimumSearchQueryLength,
    searchConfig.maxSearchSuggestionCount,
    searchConfig.searchSuggestionTypeRatio,
    selectedProfile,
    firebaseTrace
  ])
  /* --------------------------------------------- */
  /**
   * It: Sets search icon color.
   */
  useEffect(() => {
    let seachIconColor = 'transparent'
    if ((isFocus || searchText) && utils.isNotNull(searchBarTheme, 'selected', 'image')) {
      seachIconColor = searchBarTheme.selected.image
    } else if (utils.isNotNull(searchBarTheme, 'normal', 'image')) {
      seachIconColor = searchBarTheme.normal.image
    }
    setSearchIconColor(seachIconColor)
  }, [
    isFocus,
    searchText,
    searchBarTheme
  ])
  /* --------------------------------------------- */
  /**
   * It: Sets focus.
   */
  const onFocusHandle = useCallback(() => {
    toggleIsFocus(true)
  }, [])
  /* --------------------------------------------- */
  /**
   * It: Removes search text.
   * It: Removes focus.
   */
  const handleSearchBackBtnClick = useCallback(() => {
    if (/^\s/.test(searchText)) return

    updateSuggestions([])
    dispatch({ type: 'SEARCH_TEXT', value: '' })
    toggleIsFocus(false)
    setIsResponsiveSearchOpen(false)
  }, [dispatch, searchText])
  /* --------------------------------------------- */
  const onBlurHandle = useCallback((event) => {
    if (searchBar.current && !searchBar.current.contains(event.target)) toggleIsFocus(false)
  }, [])
  /* --------------------------------------------- */
  /**
   * It: Passes search text to search page.
   * It: Redirects to search page.
   */
  const handleSearchIconClick = useCallback(() => {

    if (searchText.trim().length < searchConfig.minimumSearchQueryLength ) return; // || /^\s/.test(searchText)
    const isSearchErrorShown = (errorPage || {}).type === 'GenericNoSearch'
    const query = `${routeMap.listing}?search=${window.encodeURIComponent(searchText)}&sysId=${sysId}`
    handleSearchBackBtnClick()
    toggleIsFocus(false)
    if (searchText) {
      if (isSearchErrorShown) {
        history.replace(query)
      } else {
        history.push(query)
      }
    }
  }, [
    sysId,
    history,
    errorPage,
    searchText,
    handleSearchBackBtnClick,
    searchConfig.minimumSearchQueryLength
  ])
  /* --------------------------------------------- */
  /**
   * It: On enter key press it passes search text to search page.
   * It: Redirects to search page.
   */
  const handleKeyPress = useCallback((e) => {
    searchInput.current.focus()

    if (e.key === 'Enter') {
      if (searchText.trim().length < searchConfig.minimumSearchQueryLength ) return //|| /^\s/.test(searchText)
      searchInput.current.blur()
      handleSearchIconClick()
    }
  }, [handleSearchIconClick, searchText, searchConfig.minimumSearchQueryLength])
  /* --------------------------------------------- */
  /**
   * It: Passes search text to search page.
   * It: Redirects to search page.
   */
  const handleRecentSearchClick = useCallback((searchText) => {
    const query = `${routeMap.listing}?search=${window.encodeURIComponent(searchText)}&sysId=${sysId}&recentSearch=true`
    const isSearchErrorShown = (errorPage || {}).type === 'GenericNoSearch'
    handleSearchBackBtnClick()
    searchInput.current.blur()
    if (searchText) {
      if (isSearchErrorShown) {
        history.replace(query)
      } else {
        history.push(query)
      }
    }
  }, [
    sysId,
    history,
    errorPage,
    handleSearchBackBtnClick
  ])
  /* --------------------------------------------- */
  const resSearchClickhandle = () => {
    setIsResponsiveSearchOpen(true)
    searchInput.current.focus()
  }
  /* --------------------------------------------- */
  /**
   * It: On enter key press it passes search text to search page.
   * It: Redirects to search page.
   */
  const handleSearchSuggestionClick = useCallback((title, type, uid, searchMode) => {
    if (!appConfig.baseUrl) return
     sendAnalytics({
       type: 'search',
       attr: { search_term: searchText }
       })
    /* --------------------------------------------- */
    const appMessages = appMessagesRef.current || {}
    /* --------------------------------------------- */
    const typeMap = {
      match: appMessages.media_type_match,
      movie: appMessages.media_type_movie,
      series: appMessages.media_type_series,
      sportingevent: appMessages.media_type_sportingevent,
      sportingEvent: appMessages.media_type_sportingEvent,
      preview: appMessages.media_type_preview
    }
    /* --------------------------------------------- */
    const typeMapId = {
      match: appMessages.media_type_match_id,
      movie: appMessages.media_type_movie_id,
      series: appMessages.media_type_series_id,
      sportingevent: appMessages.media_type_sportingevent_id,
      sportingEvent: appMessages.media_type_sportingEvent_id,
      preview: appMessages.media_type_preview_id
    }
    /* --------------------------------------------- */
    let profileId = selectedProfile ? selectedProfile.profileId : ''
    if (isAuthenticated) {
      setRecentSearch(appConfig.baseUrl, title, profileId).catch(() => { })
    }
    /* --------------------------------------------- */
    handleSearchBackBtnClick()
    /* --------------------------------------------- */
    if (searchMode === 'title') {
      if (type === routeVarMap.type.channel) {
        history.push(`${routeMap.player}?channelId=${uid}`)
      } else if (type === routeVarMap.type.tournament) {
        history.push(`${routeMap.listing}?filterBy=tournament&filter=${uid}&sysId=${sysId}`)
      } else {
        const noWhiteSpaceTitle = (title || '').replace(/\s/g, '-').replace(/[$&+,/:;=?@"<>'#%{}|\\^~[\]]/g, '')
        history.push(routeMap.details.replace(':pageName', typeMap[type]).replace(':type', typeMapId[type]).replace(':mediaId', `${uid}_${noWhiteSpaceTitle}`))
      }
    } else {
      const query = `${routeMap.listing}?search=${window.encodeURIComponent(title)}&mode=${searchMode.toLowerCase()}&sysId=${sysId}`
      const isSearchErrorShown = (errorPage || {}).type === 'GenericNoSearch'
      if (isSearchErrorShown) {
        history.replace(query)
      } else {
        history.push(query)
      }
    }
  }, [appConfig.baseUrl, selectedProfile, isAuthenticated, setRecentSearch, handleSearchBackBtnClick, history, sysId, errorPage, sendAnalytics, searchText])
  /* --------------------------------------------- */
  /**
   * It: Clears recent search from local.
   * It: Displays no recent search text.
   * It: Calls clear recent search api.
   */
  const handleClearAllClick = useCallback(() => {
    setRecentSearches([])
    setRecentSearchesLocal([])
    toggleNoRecentSearch(true)
    let profileId = selectedProfile ? selectedProfile.profileId : ''
    clearRecentSearch(appConfig.baseUrl, profileId).catch(() => { })
    /* --------------------------------------------- */
    log({
      type: 'Info',
      attr: {
        module: 'Search',
        event: 'recent_searche_cleared'
      }
    })
    /* --------------------------------------------- */
    searchInput.current.focus()
  }, [
    appConfig.baseUrl,
    setRecentSearches,
    clearRecentSearch,
    selectedProfile,
    log
  ])
  /* --------------------------------------------- */
  /**
   * It: Shows title for recent search & search suggetion.
   */
  const showTitle = useCallback(() => {
    if (searchText.trim().length < searchConfig.minimumSearchQueryLength) { //  && !/^\s/.test(searchText)
      if (recentSearches && recentSearches.length > 0) {
        return (
          <div className='suggestion-title'>
            <ListTitle className='search-heading' theme={searchHistoryTheme}>{appMessages.search_recent_search}</ListTitle>
            <ClearAll className='search-heading clear-all' theme={searchHistoryTheme} onClick={handleClearAllClick}>{appMessages.search_clear_all}</ClearAll>
          </div>
        )
      } else if (!recentSearches || noRecentSearch) {
        return (
          <ListTitle theme={searchSuggestionTheme} className='search-heading'>{appMessages.search_no_recent_search}</ListTitle>
        )
      } else {
        return (
          <div className='spinner-main-wrapper'>
            <Spinner size={25} />
          </div>
        )
      }
    } else {
      if (suggestions.length > 0) {
        return (
          <ListTitle theme={searchSuggestionTheme} className='search-heading'>{appMessages.search_suggestion}</ListTitle>
        )
      } else if (noMatch) {
        return (
          <ListTitle theme={searchSuggestionTheme} className='search-heading'>{appMessages.search_no_results}</ListTitle>
        )
      } else if (searchText.trim().length >= searchConfig.minimumSearchQueryLength) { //  && !/^\s/.test(searchText)
        return (
          <div className='spinner-main-wrapper'>
            <Spinner size={25} />
          </div>
        )
      }
    }
  }, [
    noMatch,
    noRecentSearch,
    searchText,
    suggestions.length,
    searchHistoryTheme,
    handleClearAllClick,
    searchSuggestionTheme,
    recentSearches,
    appMessages.search_clear_all,
    appMessages.search_no_results,
    appMessages.search_suggestion,
    appMessages.search_recent_search,
    appMessages.search_no_recent_search,
    searchConfig.minimumSearchQueryLength
  ])
  /* --------------------------------------------- */
  /**
   * It: Shows recent search & search suggestion items.
   */
  const showItems = useCallback(() => {
    if (searchText.trim().length < searchConfig.minimumSearchQueryLength) { //  && !/^\s/.test(searchText)
      if (recentSearches && recentSearches.length > 0) {
        return (
          <ul>
            {
              recentSearches.map((searchText, i) => (
                <li key={i}>
                  <div onClick={() => handleRecentSearchClick(searchText)}>
                    <ListItemTitle theme={searchHistoryTheme} className='title'>{searchText}</ListItemTitle>
                  </div>
                </li>
              )).slice(0, searchConfig.maxSearchHistoryCount)
            }
          </ul>
        )
      }
    } else {
      if (suggestions.length > 0) {
        return (
          <ul>
            {
              suggestions.map(({ title, genre, type, uid, subTitle, searchMode }, i) => {
                let typeText = ''
                if (searchMode === 'title') {
                  typeText = appMessages[`label_type_${type}`]
                } else {
                  if (searchMode === 'genre') typeText = appMessages.label_search_genre
                  else if (searchMode === 'cast') typeText = appMessages.label_search_cast
                  else if (searchMode === 'director') typeText = appMessages.label_search_director
                }
                return (
                  <li key={i}>
                    <div onClick={() => handleSearchSuggestionClick(title, type, uid, searchMode)}>
                      {appConfig.type === 'Sports' && subTitle && <ListItemSubtitle theme={searchSuggestionTheme}>{subTitle}</ListItemSubtitle>}
                      <ListItemTitle theme={searchSuggestionTheme} className='title'>{title}</ListItemTitle>
                      {
                        appConfig.type === 'Entertainment' && (
                          <div className='suggestion-list-item-bottom'>
                            {searchMode === 'title'
                              ? (
                                <>
                                  {type && <ListItemSubtitle theme={searchSuggestionTheme}>{typeText}</ListItemSubtitle>}
                                  {genre && genre.length > 0 && <ListItemSubtitle theme={searchSuggestionTheme}>{genre[0]}</ListItemSubtitle>}
                                </>)
                              : searchMode && <ListItemSubtitle theme={searchSuggestionTheme}>{typeText}</ListItemSubtitle>}
                          </div>
                        )
                      }
                    </div>
                  </li>
                )
              }).slice(0, searchConfig.maxSearchSuggestionCount)
            }
          </ul>
        )
      }
    }
  }, [
    suggestions,
    recentSearches,
    appConfig.type,
    searchText,
    searchHistoryTheme,
    searchSuggestionTheme,
    handleRecentSearchClick,
    handleSearchSuggestionClick,
    searchConfig.maxSearchHistoryCount,
    searchConfig.minimumSearchQueryLength,
    searchConfig.maxSearchSuggestionCount,
    appMessages
  ])
  /* --------------------------------------------- */
  /**
   * It: Passes search text to search page.
   * It: Redirects to search page.
   */
  const handleMoreClick = useCallback(() => {
    handleSearchIconClick()
  }, [
    handleSearchIconClick
  ])
  /* --------------------------------------------- */
  return (
    <StyleWrapper seachTheme={searchSuggestionTheme} theme={searchBarTheme} isResponsiveSearchOpen={isResponsiveSearchOpen} isFocus={isFocus || searchText} className={`search-bar ${isResponsiveSearchOpen ? 'responsiveSearchOpen' : ''}`} ref={searchBar}>
      <SearchInputOuter theme={searchBarTheme} isFocus={isFocus || searchText} className={`search-bar__input ${(isFocus || searchText) ? 'search-bar__input---focus' : ''}`}>
        <input
          ref={searchInput}
          theme={searchBarTheme}
          className='textbox'
          placeholder={appMessages.label_search_hint}
          value={searchText}
          onKeyPress={handleKeyPress}
          onChange={searchInputChangeHandler}
          autoComplete='off'
          onFocus={onFocusHandle}
          onBlur={onBlurHandle}
          spellCheck='false'
        />
        <SearchIcon
          color={searchIconColor}
          onClick={handleSearchIconClick}
        />
      </SearchInputOuter>
      <div className='searchbar-res'>
        <div className='back-btn' onClick={handleSearchBackBtnClick}><GoBackArrowIcon color={searchIconColor} style={{ width: 16, height: 16 }} /></div>
        <SearchIcon
          color={searchIconColor}
          onClick={resSearchClickhandle}
        />
      </div>
      { // && !/^\s/.test(searchText)
        isFocus  && ((searchText.trim().length < searchConfig.minimumSearchQueryLength && searchConfig.enableSearchHistory) || (searchText.length >= searchConfig.minimumSearchQueryLength && searchConfig.enableSearchSuggestions)) && (
          <div className='search-bar__suggestion'>
            <div className='suggestion-list'>
              {showTitle()}
              {showItems()}
              {isShowMore && searchText.length && suggestions.length ? <MoreResults theme={searchSuggestionTheme} onClick={handleMoreClick} className='show-more-label'>{appMessages.search_more_results}</MoreResults> : null}
            </div>
          </div>
        )
      }
      {isFocus && <div className='search-bar__suggestion search-bar__suggestion-responsive' />}
    </StyleWrapper>
  )
}



/* --------------------------------------------- */
export default SearchBar
/* --------------------------------------------- */
